/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosPagination } from "@components/elements/molecules/pagination/CosmosPagination";
import { CosmosEnumResellerContinents, ResellersSearchSectionFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { Grid } from "@panda/jsx";
import { useEffect, useMemo } from "react";
import { ResellerSearchCard } from "./ResellersSearchCard.molecule";
import { ResellersSearchNoResults } from "./ResellersSearchNoResults.element";

const gridStyles = css({
	w: "full",
	gridTemplateColumns: "repeat(3, 1fr)",
	xlDown: { gridTemplateColumns: "repeat(2, 1fr)" },
	mdDown: { gridTemplateColumns: "repeat(1, 1fr)" },
});

export type ResellersSearchListMoleculeProps = Pick<
	ResellersSearchSectionFragment,
	"cards" | "noResultsLabel" | "viewLocationLabel"
> & {
	currentPage: number;
	pageSize?: number;
	query: string;
	filter: string;
	locationLinkTemplate?: string | null;
	handlePageChange: (n: number) => void;
};

export const ResellersSearchList = ({
	cards,
	currentPage,
	pageSize = 18,
	query,
	filter,
	locationLinkTemplate,
	handlePageChange,
	viewLocationLabel,
	noResultsLabel,
}: ResellersSearchListMoleculeProps): React.JSX.Element => {
	const normalizedQuery = query.trim().toLowerCase();

	const filteredCards = useMemo(() => {
		if (!cards?.length) return [];
		return cards.filter((card) => {
			const matchesContinent = !filter || card.continents?.includes(filter as CosmosEnumResellerContinents);
			const matchesQuery =
				!normalizedQuery ||
				(card &&
					[card.name, card.street, card.region, card.city, ...card.continents, card.email, card.website].some((field) =>
						field?.toLowerCase()?.includes(normalizedQuery),
					));
			return matchesContinent && matchesQuery;
		});
	}, [cards, filter, normalizedQuery]);

	const paginatedCards = useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		const end = start + pageSize;
		return filteredCards.slice(start, end);
	}, [filteredCards, currentPage, pageSize]);

	useEffect(() => {
		if (filteredCards?.length && (currentPage - 1) * pageSize > filteredCards?.length) handlePageChange(1);
	}, [filteredCards?.length, currentPage, handlePageChange, pageSize]);

	const hasResults = !!filteredCards?.length;

	if (!hasResults) return <ResellersSearchNoResults searchValue={query} noResultsLabel={noResultsLabel} />;

	return (
		<>
			<Grid className={cx("resellers-search-cards", gridStyles)} gap="eds.dimension.32">
				{paginatedCards.map((card, i) => (
					<ResellerSearchCard
						key={`reseller-card-${card?.id || i}`}
						locationLinkTemplate={locationLinkTemplate}
						viewLocationLabel={viewLocationLabel}
						{...card}
					/>
				))}
			</Grid>

			<CosmosPagination
				current={currentPage}
				pageSize={pageSize}
				total={filteredCards?.length}
				handlePageChange={handlePageChange}
				scrollTop={false}
			/>
		</>
	);
};
